import React, { useState } from 'react'
import { defineMessages } from 'react-intl'
import PropTypes from 'prop-types'

import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import orderBy from 'lodash/orderBy'
import { getDeliveryAvailableAddresses } from '@vtex/delivery-packages/dist/address'
import Modal from '@vtex/styleguide/lib/Modal'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import { addressShape } from '@/checkout-instore-legacy/shipping/shapes'
import { REQUIRED_ADDRESS_FIELDS } from '@/checkout-instore-legacy/shipping/constants'
import PostalCodeInput from '@/checkout-instore-legacy/shipping/components/PostalCode/PostalCodeInput'

import AddressSelector from './AddressSelector'

const messages = defineMessages({
  modalTitle: {
    id: 'location.modal.title',
    defaultMessage: 'Location',
  },
  labelPostalCodeIdentified: {
    id: 'location.modal.labelPostalCodeInputIdentified',
    defaultMessage: 'Insert the postal code of customer:',
  },
  labelPostalCodeAnonymous: {
    id: 'location.modal.labelPostalCodeInputAnonymous',
    defaultMessage: 'Insert the postal code of customer',
  },
  labelAddressSelector: {
    id: 'location.modal.labelAddressSelector',
    defaultMessage: 'Select an address:',
  },
})

function LocationModal({
  availableAddresses,
  intl: { formatMessage },
  isAnonymousName,
  isModalOpen,
  handleCloseModal,
  receiverName,
  countryCode,
  setPostalCodeSession,
  isSimulationOnlyMode,
  context,
}) {
  const [modalIsClosing, setModalIsClosing] = useState(false)
  const addresses = orderBy(
    getDeliveryAvailableAddresses(availableAddresses, REQUIRED_ADDRESS_FIELDS),
    ['street']
  )

  const submitAddress = (address) => {
    const selectedAddress = {
      ...address,
      receiverName,
    }

    setPostalCodeSession(selectedAddress, isSimulationOnlyMode)

    setModalIsClosing(false)
    handleCloseModal()
  }

  return (
    <Modal
      isOpen={isModalOpen}
      title={formatMessage(messages.modalTitle)}
      responsiveFullScreen
      centered
      onClose={handleCloseModal}
    >
      <div className="near-black">
        <div>
          <p data-cy="postalcode-label" className="mb3 fw5 mb5">
            {isAnonymousName || isNil(receiverName)
              ? formatMessage(messages.labelPostalCodeAnonymous)
              : formatMessage(messages.labelPostalCodeIdentified, {
                  name: receiverName,
                })}
          </p>
          <div className="mb7">
            <PostalCodeInput
              onSubmit={submitAddress}
              countryCode={countryCode}
              disabled={modalIsClosing}
              context={context}
            />
          </div>
        </div>
        {!isEmpty(addresses) && (
          <React.Fragment>
            <p className="mt0 fw5 mb5">
              {formatMessage(messages.labelAddressSelector)}
            </p>
            <div className="mb5">
              <AddressSelector
                addresses={addresses}
                onSubmit={submitAddress}
                modalIsClosing={modalIsClosing}
                setModalIsClosing={setModalIsClosing}
              />
            </div>
          </React.Fragment>
        )}
      </div>
    </Modal>
  )
}

LocationModal.propTypes = {
  availableAddresses: PropTypes.arrayOf(addressShape),
  receiverName: PropTypes.string,
  countryCode: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  isAnonymousName: PropTypes.bool.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  handleCloseModal: PropTypes.func.isRequired,
  setPostalCodeSession: PropTypes.func.isRequired,
  isSimulationOnlyMode: PropTypes.bool,
}

export default LocationModal
